import { useQuery } from 'react-query';
import { Axios } from '@utils/api/axios-instance';
import { URL_EXTENSION } from '@utils/constants/app-constants';
import {
  OperationsLiveResponse,
  OperationsQueryParams
} from '@utils/types/back-office/operations';

/**
 * Hook to fetch live operations data from the backend
 *
 * Endpoint: GET /api/admin/operations/live
 *
 * Query params:
 *   - status (optional): filter by session status (processing, queued, uploaded, error, finished, streaming, stranded)
 *   - search (optional): search by session_id or org name
 *   - filename (optional): case-insensitive partial match on session filename
 *   - organization_id (optional): filter by organization
 *   - limit (optional): page size (default 10, max 1000)
 *   - offset (optional): page offset (default 0)
 *   - sort (optional): date_session_start, status, organization, duration
 *   - direction (optional): asc, desc
 *   - time_range (optional): "24h" | "7d" | "30d" | "all" (default: "24h")
 *   - date_from (optional): ISO 8601 datetime - overrides time_range
 *   - date_to (optional): ISO 8601 datetime - overrides time_range
 *
 * Returns unified view of:
 *   - System health metrics
 *   - Session list (consolidated from stranded, queue, traffic)
 *   - Session counts by status
 */
export const useGetOperationsLive = (
  params?: OperationsQueryParams,
  refetchInterval?: number | false
) => {
  const fetchOperationsLive = async (): Promise<OperationsLiveResponse> => {
    const queryParams = new URLSearchParams();

    if (params?.status) queryParams.append('status', params.status);
    if (params?.search) queryParams.append('search', params.search);
    if (params?.filename) queryParams.append('filename', params.filename);
    if (params?.session_id) queryParams.append('session_id', params.session_id);
    if (params?.organization_name)
      queryParams.append('organization_name', params.organization_name);
    if (params?.min_duration !== undefined)
      queryParams.append('min_duration', String(params.min_duration));
    if (params?.organization_id)
      queryParams.append('organization_id', String(params.organization_id));
    if (params?.limit) queryParams.append('limit', String(params.limit));
    if (params?.offset) queryParams.append('offset', String(params.offset));
    if (params?.sort) queryParams.append('sort', params.sort);
    if (params?.direction) queryParams.append('direction', params.direction);
    // Time range parameters - date_from/date_to override time_range on backend
    if (params?.date_from) queryParams.append('date_from', params.date_from);
    if (params?.date_to) queryParams.append('date_to', params.date_to);
    if (params?.time_range && !params?.date_from && !params?.date_to) {
      queryParams.append('time_range', params.time_range);
    }

    const queryString = queryParams.toString();
    const url = `${URL_EXTENSION}/admin/operations/live${queryString ? `?${queryString}` : ''}`;

    const response = await Axios.get<OperationsLiveResponse>(url);
    return response.data;
  };

  return useQuery<OperationsLiveResponse>(
    ['operations-live', params],
    fetchOperationsLive,
    {
      refetchInterval: refetchInterval ?? 5000, // Poll every 5 seconds by default; pass `false` to disable
      refetchOnWindowFocus: true,
      staleTime: 3000
    }
  );
};

export const OPERATIONS_LIVE_QUERY_KEY = 'operations-live';
